import { Button, Numeric } from "@rothoblaas/design-system";
import { useState, type FC } from "react";
import { Controller, useFormContext, type ControllerRenderProps } from "react-hook-form";
import useI18n from "../../../../../hooks/useTranslations";
import styles from "../../LockCalculator.module.css";
import { formatCoefficient } from "../../model/coefficientFormat";
import type { CalculationCoefficients, LockCalculatorFormValues } from "../../model/form";
import { coefficientDiffers } from "../../model/notices";
import { NoticeFormField as FormField } from "../notices/NoticeFormField";

type OverrideName =
  "environment.kmodOverride" | "environment.gammaMConnectionOverride" | "environment.gammaMAluminiumOverride";

type CoefficientInputProps = {
  field: ControllerRenderProps<LockCalculatorFormValues, OverrideName>;
  id: string;
  isInvalid: boolean;
  disabled: boolean;
};

/** Takes up to 3 decimals while typing; at rest it shows the value as formatCoefficient writes it (1.30). */
const CoefficientInput: FC<CoefficientInputProps> = ({ field, id, isInvalid, disabled }) => {
  const [isEditing, setIsEditing] = useState(false);
  const restingDecimals = field.value == null ? undefined : formatCoefficient(field.value).split(".")[1]?.length;
  return (
    <Numeric
      {...field}
      value={field.value}
      decimalScale={isEditing ? 3 : (restingDecimals ?? 3)}
      fixedDecimalScale={!isEditing && restingDecimals !== undefined}
      id={id}
      isInvalid={isInvalid}
      allowNegative={false}
      disabled={disabled}
      onFocus={() => setIsEditing(true)}
      onBlur={() => {
        setIsEditing(false);
        field.onBlur();
      }}
      onValueChange={(value) => field.onChange(value ?? null)}
    />
  );
};

interface CoefficientsFieldsProps {
  readOnly?: boolean;
  coefficients: CalculationCoefficients;
}

/**
 * The three partial coefficients as editable values, shown while the "custom" switch is on. Each keeps the
 * normative value next to it, so a custom value is always read against what the standard would give.
 */
export const CoefficientsFields: FC<CoefficientsFieldsProps> = ({ readOnly = false, coefficients }) => {
  const { translation: t } = useI18n();
  const { control } = useFormContext<LockCalculatorFormValues>();
  const env = t.lockCalculatorModule.env;

  const fields: { name: OverrideName; label: string; normative: number | null }[] = [
    { name: "environment.kmodOverride", label: env.kmod, normative: coefficients.kmodDefault },
    { name: "environment.gammaMConnectionOverride", label: env.gammaM, normative: coefficients.gammaMDefault },
    {
      name: "environment.gammaMAluminiumOverride",
      label: env.gammaMAluminium,
      normative: coefficients.gammaMAluminiumDefault,
    },
  ];

  return (
    <div className={styles.compactFormStack}>
      {fields.map(({ name, label, normative }) => (
        <Controller
          key={name}
          name={name}
          control={control}
          render={({ field }) => {
            const differs = coefficientDiffers(field.value, normative);
            return (
              <FormField name={name} label={label} isOptional>
                {({ labelId, isInvalid }) => (
                  <>
                    <CoefficientInput field={field} id={labelId} isInvalid={isInvalid} disabled={readOnly} />
                    {normative != null && (
                      <div className={styles.fieldReference}>
                        <span>
                          {env.normativeValue}: <strong>{formatCoefficient(normative)}</strong>
                        </span>
                        {differs && !readOnly && (
                          <Button variant="secondary" onClick={() => field.onChange(normative)}>
                            {env.restoreNormative}
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </FormField>
            );
          }}
        />
      ))}
    </div>
  );
};
